import React, { useCallback, useEffect } from 'react';
import {
  convertBase64ToFile,
  convertBlobUrlToDataUrl,
  getFirebaseUrlsFromFiles,
  isEmpty
} from '../../../helpers/common';
import { RecordButton } from '../styles';
import { useMediaRecorder } from '../../../hooks/useMediaRecorder';
import { captureException } from '../../../services/logService';
import { addShareData } from '../../../services/projectServices';
import { ICON_BUTTON_SIZE, ICON_BUTTON_TYPE, SHARE_TYPE } from '../../../global/constants';
import { dispatchTaskError } from '../../../services/taskServices';
import { rootStore } from '../../../mobx/rootStore';
import IconButton from '../../button/iconButton';

function RecordingComment({
  setLoading,
  setValueContent,
  isCommentModel,
  setButtonDisable,
  handleOpenModal,
  uploadSnapEmbed
}: {
  setLoading: Function;
  fieldValue: string;
  setValueContent: Function;
  isCommentModel: boolean;
  setButtonDisable: Function;
  handleOpenModal: () => void;
  uploadSnapEmbed?: (url: string, fileName: string, videoId: string) => void;
}) {
  const { status, stopRecording, mediaBlobUrl, clearBlobUrl } = useMediaRecorder();

  //mobx variables
  const { projectStore } = rootStore;
  const { currentProject } = projectStore;

  const onScreenCaptureComplete = useCallback(
    async (mediaBlobUrl: string | null) => {
      if (!mediaBlobUrl) return;
      try {
        setLoading(true);
        const baseFile = await convertBlobUrlToDataUrl(mediaBlobUrl)
          .then((regularUrl) => {
            return regularUrl;
          })
          .catch((error) => {
            captureException(error);
          });
        const fileData: File | undefined = await convertBase64ToFile(baseFile, true);
        if (!fileData) {
          dispatchTaskError('Error during file conversion');
          return false;
        }
        if (fileData?.size > 2.5e8) {
          dispatchTaskError('Please upload a file smaller than 250 MB.');
          return false;
        }
        const firebaseUrl = await getFirebaseUrlsFromFiles([{ file: fileData }], 'screen_recordings/');
        if (firebaseUrl && firebaseUrl.length > 0) {
          const sharePayload = {
            ref: firebaseUrl[0],
            type: SHARE_TYPE.snap,
            projectId: currentProject?.id
          };
          const shareResponse = await addShareData(sharePayload);
          const snapUrl = `https://share.teamcamp.app/${shareResponse?.id}`;
          rootStore.appStore.setScreenRecordingUrl(snapUrl || '');
          const fileName = fileData?.name;

          if (uploadSnapEmbed) uploadSnapEmbed(firebaseUrl[0] || '', fileName, shareResponse?.id);
        }
      } catch (e) {
        captureException(e);
      } finally {
        clearBlobUrl();
        setButtonDisable(false);
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentProject?.id, setButtonDisable, setLoading, setValueContent]
  );

  useEffect(() => {
    if (!isEmpty(mediaBlobUrl) && isCommentModel) onScreenCaptureComplete(mediaBlobUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaBlobUrl, isCommentModel]);

  useEffect(() => {
    if (status !== 'idle') {
      setButtonDisable(true);
    } else {
      setButtonDisable(false);
    }
  }, [setButtonDisable, status]);

  const onClickRecord = useCallback(() => {
    if (status !== 'recording') {
      handleOpenModal();
    } else {
      stopRecording();
    }
  }, [handleOpenModal, status, stopRecording]);

  return (
    <>
      <RecordButton>
        <IconButton
          iconName='record-start-icon'
          size={ICON_BUTTON_SIZE.MEDIUM}
          variant={ICON_BUTTON_TYPE.TERTIARY}
          onClick={() => onClickRecord()}
          className={status !== 'recording' ? '' : 'stop-recording'}
        />
      </RecordButton>
    </>
  );
}

export default RecordingComment;
